import { mdy, type Evaluation, type RequestType } from "@scheduler/shared";
import type { MouseEvent } from "react";

import { navigate } from "../router";
import { hours } from "./format";
import { ProjectionLedger } from "./ProjectionLedger";

/** To the list on My requests: scrolled to when it is on this screen, navigated to when not. */
function toMyRequests(e: MouseEvent) {
  e.preventDefault();
  const list = document.getElementById("my-requests-list");
  if (list) list.scrollIntoView({ behavior: "smooth" });
  else navigate({ kind: "mine" });
}

/**
 * "Show the working": what the gates found, in the order they run. Everything
 * here comes from the API's evaluation — the screen decides nothing.
 */
export function WorkingPanel({ evaluation, loading, approverName, type, hrCreates = false }: {
  evaluation: Evaluation | null;
  /** HR's unpaid form: creating it IS the decision, so nothing is sent to anyone to approve. */
  hrCreates?: boolean;
  /** NTD is approved on submit for everyone, and says who is told rather than who decides. */
  type: RequestType;
  loading: boolean;
  approverName: string | null;
}) {
  if (!evaluation) {
    return <div className="calc calc-empty">{loading ? "Checking…" : "Pick dates to see the working."}</div>;
  }
  const bd = evaluation.breakdown;
  const pr = evaluation.projection;
  const failure = evaluation.failure;

  return (
    <div className="calc" aria-live="polite">
      <div className="calc-head">
        <h3>Availability check</h3>
        {loading ? <span className="muted">updating…</span> : null}
      </div>
      <div className="calc-body">
        {bd ? (
          <>
            <table className="ledger">
              <tbody>
                <tr><td>Calendar days</td><td>{bd.calendar}</td></tr>
                <tr className="sub"><td>Weekend days</td><td>−{bd.weekend}</td></tr>
                <tr className="sub">
                  <td>Company holidays{bd.holidayNames.length ? <span className="why">{bd.holidayNames.join(", ")}</span> : null}</td>
                  <td>−{bd.holiday}</td>
                </tr>
                <tr className="total"><td>Working days</td><td>{bd.counted}</td></tr>
                {evaluation.chargedHours > 0 ? (
                  <tr className="total big"><td>Charged</td><td>{hours(evaluation.chargedHours)} h</td></tr>
                ) : null}
              </tbody>
            </table>
            <div className="daystrip">
              {bd.days.map((d) => {
                const h = evaluation.days.find((x) => x.date === d.date)?.hours;
                const kind = d.kind === "WORKING" ? (h !== undefined && h < 8 ? "part" : "count") : d.kind === "HOLIDAY" ? "hol" : "skip";
                return (
                  <span key={d.date} className={`daychip ${kind}`} title={d.holidayName ?? (d.kind === "WEEKEND" ? "Weekend" : "Working day")}>
                    {mdy(d.date)}{h !== undefined && h < 8 ? ` · ${h}h` : ""}
                  </span>
                );
              })}
            </div>
          </>
        ) : null}

        {pr ? <ProjectionLedger projection={pr} requested={evaluation.chargedHours} /> : null}

        {/* The kind's limit: bereavement's, jury duty's left that year, volunteer time's left in 365 days. */}
        {evaluation.allowances.map((a) => (
          <p key={`${a.kind}-${a.year ?? ""}`} className="muted">
            {a.kind === "BEREAVEMENT"
              ? `Bereavement for this relationship is up to ${hours(a.limitHours)} h; this asks for ${hours(a.requestedHours)} h.`
              : a.kind === "VOLUNTEER"
              ? `Volunteer time: ${hours(Math.max(0, a.limitHours - a.usedHours))} h of ${hours(a.limitHours)} h left in any 365 days around this; this asks for ${hours(a.requestedHours)} h.`
              : `Jury duty in ${a.year}: ${hours(Math.max(0, a.limitHours - a.usedHours))} h of ${hours(a.limitHours)} h left before this; this asks for ${hours(a.requestedHours)} h.`}
          </p>
        ))}

        {evaluation.warnings.map((w) => (
          <p key={w.code} className="flag"><strong>Note:</strong> {w.message}</p>
        ))}

        {failure?.ownRequests ? (
          // Their own request on these days (PR 9): something they already did, not an obstacle.
          <div className="verdict mine">
            <span className="mark">Already asked</span>
            <span>{failure.message} <a href="/requests" onClick={toMyRequests}>See it under My requests</a></span>
          </div>
        ) : failure ? (
          <div className="verdict fail"><span className="mark">Blocked</span><span>{failure.message}</span></div>
        ) : evaluation.outcome?.kind === "AUTO_APPROVE" && type === "NTD" ? (
          <div className="verdict pass"><span className="mark">Auto</span><span>Approved on submit. Your supervisor and HR are told.</span></div>
        ) : evaluation.outcome?.kind === "AUTO_APPROVE" ? (
          <div className="verdict pass"><span className="mark">Auto</span><span>Passes every check and is approved on submit.</span></div>
        ) : evaluation.outcome && hrCreates ? (
          <div className="verdict pass"><span className="mark">Clears</span><span>Passes every check. Creating it approves it, and the employee is told.</span></div>
        ) : evaluation.outcome?.kind === "ROUTE_TO_HR" ? (
          <div className="verdict pass"><span className="mark">Clears</span><span>Passes every check. Submitting sends it to <strong>HR</strong> to approve or deny.</span></div>
        ) : evaluation.outcome?.kind === "FIELD_PENDING" ? (
          <div className="verdict pass"><span className="mark">Clears</span><span>Passes every check. It will be pending until the project's PM, or HR, approves it.</span></div>
        ) : evaluation.outcome ? (
          <div className="verdict pass">
            <span className="mark">Clears</span>
            <span>Passes every check. Submitting sends it to <strong>{approverName ?? "HR"}</strong> to approve or deny.</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
